import { useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { CasAuthApi, CasProfileApi, CasPurposesApi, getUser } from "@jti/rest-api";
import { type PurposeType } from "@jti/rest-api-types";

export enum Conditions {
  LOGGED_IN = "LOGGED_IN",
  AGE_VERIFIED = "AGE_VERIFIED",
  DEVICE_REGISTRATION = "DEVICE_REGISTRATION",
  PURPOSE = "PURPOSE",
}
export type RestrictedConditionType = {
  condition: Conditions;
  status: boolean | PurposeType[];
  name?: string;
};
export type RestrictedContentReturnType = {
  restrictedConditions: RestrictedConditionType[] | null;
  isRestricted: boolean;
  isLoyaltyEnabled: boolean;
};

export const useLoyaltyRestrictedContent = (): RestrictedContentReturnType => {
  const [restrictedConditions, setRestrictedConditions] = useState<RestrictedConditionType[] | null>(null);
  const [isRestricted, setIsRestricted] = useState<boolean>(true);
  const {
    loyaltyRestrictionPurpose,
    isDeviceRegisteredRestriction,
    IS_LOYALTY_ENABLED,
    isAgeVerifiedLoyaltyRestriction,
    isLoyaltyContentRestricted,
  } = useConfig();

  const checkLoginRestriction = async () => {
    try {
      const user = getUser();
      let sessionValidationStatus = false;
      if (user?.ciam_token) {
        const { statusCode } = await CasAuthApi.init().validate(user.ciam_token, true);
        sessionValidationStatus = statusCode === 200 || statusCode === 204;
      }
      return sessionValidationStatus;
    } catch (error) {
      console.error("Error while checking login restriction", error);
      return false;
    }
  };
  const checkAgeRestriction = async (sessionValidationStatus: boolean) => {
    if (!sessionValidationStatus) {
      return false;
    }
    try {
      const { is_age_verified } = await CasProfileApi.init().getProfile();
      return is_age_verified;
    } catch (error) {
      console.error("Error while checking age restriction", error);
      return false;
    }
  };
  const checkPurposeRestriction = async (sessionValidationStatus: boolean): Promise<PurposeType[]> => {
    if (!sessionValidationStatus) {
      return [];
    }
    try {
      const data = await CasPurposesApi.init().getPurposesForUserByCategory("all", true);

      const purposesStatusList = data
        .filter(({ id }) => loyaltyRestrictionPurpose.includes(id))
        .map(({ id, accepted }) => ({ id, accepted }));
      return purposesStatusList;
    } catch (error) {
      console.error("Error while checking purpose restriction", error);
      return [];
    }
  };
  useEffect(() => {
    const getRestrictionDetails = async () => {
      const conditions: RestrictedConditionType[] = [];
      if (isLoyaltyContentRestricted) {
        const sessionValidationStatus = await checkLoginRestriction();
        conditions.push({ condition: Conditions.LOGGED_IN, status: sessionValidationStatus });
        if (isAgeVerifiedLoyaltyRestriction) {
          const isAgeVerified = await checkAgeRestriction(sessionValidationStatus);
          conditions.push({ condition: Conditions.AGE_VERIFIED, status: isAgeVerified });
        }
        if (loyaltyRestrictionPurpose.length > 0) {
          const purposesStatusList = await checkPurposeRestriction(sessionValidationStatus);
          conditions.push({ condition: Conditions.PURPOSE, status: purposesStatusList });
        }
        if (isDeviceRegisteredRestriction) {
          conditions.push({
            condition: Conditions.DEVICE_REGISTRATION,
            status: getUser().is_device_registered || false,
          });
        }
      }

      if (conditions.length > 0) {
        setRestrictedConditions(conditions);
      } else {
        setIsRestricted(IS_LOYALTY_ENABLED !== "true");
      }
    };
    getRestrictionDetails().catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    if (restrictedConditions) {
      const singleRestriction = (status: boolean) => !status;

      const multiRestriction = (status: PurposeType[]) => {
        return status.length > 0 && !status.every(({ accepted }) => accepted);
      };

      const hasRestrictedConditions = restrictedConditions.some(({ status }) => {
        if (Array.isArray(status)) {
          return multiRestriction(status);
        } else {
          return singleRestriction(status);
        }
      });
      setIsRestricted(hasRestrictedConditions);
    }
  }, [restrictedConditions]);

  return { restrictedConditions, isRestricted, isLoyaltyEnabled: IS_LOYALTY_ENABLED === "true" };
};
